import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import PdfPreview from "../../../Common/PDFPreview";
import { RingLoader } from 'react-spinners';
import { formatReadableAmount } from "../../../Common/FormatFunctions/FormatAmount"
import BackButton from "../../../Common/Buttons/BackButton";

const API_URL = process.env.REACT_APP_API;

const StockBookReportMillwise = () => {
  const location = useLocation();
  const companyCode = sessionStorage.getItem("Company_Code");
  const Year_Code = sessionStorage.getItem("Year_Code");
  const companyName = sessionStorage.getItem('Company_Name');

  const searchParams = new URLSearchParams(location.search);
  const itemCode = searchParams.get('itemCode');
  const toDate = searchParams.get('toDate');
  const fromDate = searchParams.get('fromDate');

  const [groupedData, setGroupedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [pdfPreview, setPdfPreview] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  const navigate = useNavigate();

  const getKey = (obj, candidates) => {
    for (const k of candidates) if (k in obj) return k;
    return candidates[candidates.length - 1]; // fallback last
  };

  const groupBy = (array, keys) => {
    // keys = ['mill_name','Mill_Name','millname'] etc.
    if (!array || array.length === 0) return {};
    const primaryKey = getKey(array[0], keys);

    const grouped = array.reduce((acc, item) => {
      const groupKey = (item[primaryKey] ?? 'Unknown').toString();
      if (!acc[groupKey]) acc[groupKey] = [];
      acc[groupKey].push(item);
      return acc;
    }, {});

    const sortedKeys = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
    const sorted = {};
    sortedKeys.forEach(k => (sorted[k] = grouped[k]));
    return sorted;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
  };

  useEffect(() => {
    const fetchStockBookReport = async () => {
      try {
        const response = await fetch(
          `${API_URL}/report-stock-book-millwise?fromDate=${fromDate}&company_code=${companyCode}&Item_Code=${itemCode ?? ''}&ToDate=${toDate}&Year_Code=${Year_Code}`
        );
        const result = await response.json();

        const filteredData = (result.data || []).filter((item) => {
          const docDate = new Date(item.doc_date);
          const from = new Date(fromDate);
          const to = new Date(toDate);
          return docDate >= from && docDate <= to;
        });

        // GROUP BY MILL (supports multiple common field names)
        const grouped = groupBy(filteredData, ["mill_name", "Mill_Name", "millname", "MillShortName"]);
        setGroupedData(grouped);
      } catch (error) {
        console.error("Error fetching stock book report:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStockBookReport();
  }, [fromDate, toDate, companyCode, Year_Code, itemCode]);

  const handleBack = () => navigate('/stock-book');

  const generatePDF = () => {
    const doc = new jsPDF("landscape", "mm", "a4");
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;
    const margin = 10;

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    const companyNameWidth = doc.getTextWidth(companyName || "Company Name");
    doc.text(companyName || "Company Name", (pageWidth - companyNameWidth) / 2, 10);

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    const titleWidth = doc.getTextWidth("Stock Book Report");
    doc.text("Stock Book Report", (pageWidth - titleWidth) / 2, 18);

    doc.setFontSize(10);
    const dateRangeText = `From Date: ${formatDate(fromDate)} | To Date: ${formatDate(toDate)}`;
    const dateRangeWidth = doc.getTextWidth(dateRangeText);
    doc.text(dateRangeText, (pageWidth - dateRangeWidth) / 2, 24);

    let currentY = 34;
    Object.keys(groupedData).forEach((group) => {
      if (currentY + 20 > pageHeight - margin) {
        doc.addPage();
        currentY = margin;
      }

      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      const headText = `Mill: ${group.toUpperCase()}`;
      const headWidth = doc.getTextWidth(headText);
      doc.text(headText, (pageWidth - headWidth) / 2, currentY);
      currentY += 10;

      const rows = groupedData[group] || [];

      const tableData = rows.map((row) => [
        formatDate(row.doc_date),
        formatReadableAmount((parseFloat(row.op_qty) || 0).toFixed(2)),
        formatReadableAmount((parseFloat(row.op_value) || 0).toFixed(2)),
        formatReadableAmount((parseFloat(row.purc_qty) || 0).toFixed(2)),
        formatReadableAmount((parseFloat(row.purc_value) || 0).toFixed(2)),
        formatReadableAmount((parseFloat(row.sale_qty) || 0).toFixed(2)),
        formatReadableAmount((parseFloat(row.sale_val) || 0).toFixed(2)),
        formatReadableAmount((parseFloat(row.close_qty) || 0).toFixed(2)),
        formatReadableAmount((parseFloat(row.close_val) || 0).toFixed(2)),
      ]);

      const totalRow = [
        "Total",
        "", // opening totals not meaningful as sums; keep blank
        "",
        formatReadableAmount(
          rows.reduce((sum, it) => sum + (parseFloat(it.purc_qty) || 0), 0).toFixed(2)
        ),
        formatReadableAmount(
          rows.reduce((sum, it) => sum + (parseFloat(it.purc_value) || 0), 0).toFixed(2)
        ),
        formatReadableAmount(
          rows.reduce((sum, it) => sum + (parseFloat(it.sale_qty) || 0), 0).toFixed(2)
        ),
        formatReadableAmount(
          rows.reduce((sum, it) => sum + (parseFloat(it.sale_val) || 0), 0).toFixed(2)
        ),
        "", // closing totals shown line-wise; optional to compute last close
        "",
      ];

      doc.autoTable({
        startY: currentY,
        head: [[
          "Date",
          "Opening Qty",
          "Opening Value",
          "Purchase Qty",
          "Purchase Value",
          "Sale Qty",
          "Sale Value",
          "Closing Qty",
          "Closing Value",
        ]],
        body: [...tableData, totalRow],
        styles: { fontSize: 8, cellPadding: 3, halign: "right" },
        headStyles: { fillColor: [240, 240, 240], textColor: 0 },
        columnStyles: { 0: { halign: "center" } },
        didDrawPage: (data) => {
          currentY = data.cursor.y;
        },
      });

      currentY = doc.lastAutoTable.finalY + 10;
    });

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    setPdfPreview(pdfUrl);
    setShowPreview(true);
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <RingLoader />
      </div>
    );
  }

  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
      <div className="d-flex mb-3 mt-0.5">
        <BackButton onClick={handleBack} />
        {showPreview && pdfPreview && (
          <PdfPreview pdfData={pdfPreview} apiData={groupedData} label={"Stock-Report"} />
        )}
        <button
          onClick={generatePDF}
          className="print-button"
          style={{ fontSize: "16px", height: "50px", marginTop: "10px" }}
        >
          PDF Preview
        </button>
      </div>

      <h2 style={{ textAlign: "center", marginBottom: "5px", color: "#333", marginTop: "-60px" }}>
        {companyName}
      </h2>
      <p style={{ textAlign: "center", fontSize: "14px", marginBottom: "20px" }}>
        <strong>Stock Book Report From Date:</strong> {formatDate(fromDate)} |{" "}
        <strong>To Date:</strong> {formatDate(toDate)}
      </p>

      {Object.keys(groupedData).map((group, index) => (
        <div key={index} style={{ marginBottom: "30px" }}>
          <h3 style={{ backgroundColor: "#f4f4f4", padding: "10px", textAlign: "left" }}>
            {`MILL: ${group.toUpperCase()}`}
          </h3>
          <table border="1" style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
            <thead>
              <tr>
                <th rowSpan="2" style={{ textAlign: "center", padding: "10px" }}>Date</th>
                <th colSpan="3" style={{ textAlign: "center", padding: "10px" }}>Opening</th>
                <th colSpan="1" style={{ textAlign: "center", padding: "10px" }}>Purchase</th>
                <th colSpan="3" style={{ textAlign: "center", padding: "10px" }}>Sale</th>
                <th colSpan="2" style={{ textAlign: "center", padding: "10px" }}>Closing</th>
              </tr>
              <tr>
                <th style={{ textAlign: "right", padding: "10px" }}>Quantity (in Quintals)</th>
                <th style={{ textAlign: "right", padding: "10px" }}>Value</th>
                <th style={{ textAlign: "right", padding: "10px" }}>Quantity (in Quintals)</th>
                <th style={{ textAlign: "right", padding: "10px" }}>Value</th>
                <th style={{ textAlign: "right", padding: "10px" }}>Quantity (in Quintals)</th>
                <th style={{ textAlign: "right", padding: "10px" }}>Value</th>
                <th style={{ textAlign: "right", padding: "10px" }}>Quantity (in Quintals)</th>
                <th style={{ textAlign: "right", padding: "10px" }}>Value</th>
              </tr>
            </thead>
            <tbody>
              {(groupedData[group] || []).map((row, rowIndex) => (
                <tr key={rowIndex} style={{ backgroundColor: rowIndex % 2 === 0 ? "#f9f9f9" : "white" }}>
                  <td style={{ textAlign: "center" }}>{formatDate(row.doc_date)}</td>
                  <td style={{ textAlign: "right" }}>
                    {formatReadableAmount((parseFloat(row.op_qty) || 0).toFixed(2))}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {formatReadableAmount((parseFloat(row.op_value) || 0).toFixed(2))}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {formatReadableAmount((parseFloat(row.purc_qty) || 0).toFixed(2))}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {formatReadableAmount((parseFloat(row.purc_value) || 0).toFixed(2))}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {formatReadableAmount((parseFloat(row.sale_qty) || 0).toFixed(2))}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {formatReadableAmount((parseFloat(row.sale_val) || 0).toFixed(2))}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {formatReadableAmount((parseFloat(row.close_qty) || 0).toFixed(2))}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {formatReadableAmount((parseFloat(row.close_val) || 0).toFixed(2))}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot style={{ backgroundColor: "#e0e0e0", fontWeight: "bold" }}>
              <tr>
                <td style={{ textAlign: "center" }}>Total</td>
                <td />
                <td />
                <td style={{ textAlign: "right" }}>
                  {formatReadableAmount(
                    (groupedData[group] || []).reduce((sum, it) => sum + (parseFloat(it.purc_qty) || 0), 0).toFixed(2)
                  )}
                </td>
                <td style={{ textAlign: "right" }}>
                  {formatReadableAmount(
                    (groupedData[group] || []).reduce((sum, it) => sum + (parseFloat(it.purc_value) || 0), 0).toFixed(2)
                  )}
                </td>
                <td style={{ textAlign: "right" }}>
                  {formatReadableAmount(
                    (groupedData[group] || []).reduce((sum, it) => sum + (parseFloat(it.sale_qty) || 0), 0).toFixed(2)
                  )}
                </td>
                <td style={{ textAlign: "right" }}>
                  {formatReadableAmount(
                    (groupedData[group] || []).reduce((sum, it) => sum + (parseFloat(it.sale_val) || 0), 0).toFixed(2)
                  )}
                </td>
                <td />
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      ))}
    </div>
  );
};

export default StockBookReportMillwise;

